import { Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/app-text';
import { IconTile } from '@/components/ui/icon-tile';
import { colors, radius, shadows, spacing } from '@/theme/tokens';
import type { Reminder } from '@/types/domain';
import { daysUntil } from '@/utils/format';

const TYPE_ICON: Record<Reminder['type'], keyof typeof Ionicons.glyphMap> = {
  OC_END: 'car-outline',
  PROTECTION_END: 'shield-outline',
  INSTALLMENT: 'cash-outline',
  TECH_EXAM: 'construct-outline',
  POLICY_ANNIVERSARY: 'calendar-outline',
  FIXED_RATE_END: 'trending-up-outline',
  FIXED_COST: 'receipt-outline',
  CUSTOM: 'notifications-outline',
};

interface EventRowProps {
  reminder: Reminder;
  onPress?: () => void;
  /** optional right-side element (e.g. an enable Switch); falls back to a chevron */
  trailing?: React.ReactNode;
}

/** Upcoming-event card: left accent border (red when urgent), icon tile, title, "X • Za N dni" */
export function EventRow({ reminder, onPress, trailing }: EventRowProps) {
  const { t } = useTranslation();
  const days = daysUntil(reminder.eventDate);
  const urgent = days <= 7 && reminder.enabled;
  const accent = urgent ? colors.error : colors.navy;
  const tileBg = urgent ? colors.errorBg : colors.infoBg;
  const tileFg = urgent ? colors.error : colors.navy;

  const whenLabel =
    days === 0 ? t('common.today') : days === 1 ? t('common.tomorrow') : t('common.inDays', { count: days });

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        backgroundColor: colors.card,
        borderRadius: radius.card,
        borderLeftWidth: 4,
        borderLeftColor: accent,
        padding: spacing.md,
        transform: [{ scale: pressed ? 0.98 : 1 }],
        ...shadows.card,
      })}
    >
      <IconTile icon={TYPE_ICON[reminder.type]} bg={tileBg} fg={tileFg} />
      <View style={{ flex: 1 }}>
        <AppText variant="labelLg">{reminder.title}</AppText>
        <AppText variant="bodyMd">
          {reminder.subtitle ? `${reminder.subtitle} • ` : ''}
          {whenLabel}
        </AppText>
      </View>
      {trailing ?? <Ionicons name="chevron-forward" size={20} color={colors.outlineVariant} />}
    </Pressable>
  );
}
